<template>
  <SmTabs :tabs="tabs" />
</template>

<script setup lang="ts">
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import SmTabs from '@/components/sm/SmTabs.vue'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'

  const route = useRoute()
  const { t } = useI18n()
  const applicationDetailsStore = useApplicationDetailsStore()

  const normalizedRouteSubAccountId = computed(() => {
    const rawParam = route.params.subAccountId
    if (typeof rawParam !== 'string') return undefined
    try {
      return decodeURIComponent(rawParam)
    } catch {
      return rawParam
    }
  })

  const activeSubAccountId = computed(() => {
    return (
      normalizedRouteSubAccountId.value ||
      applicationDetailsStore.selectedSubAccount?.id ||
      undefined
    )
  })

  const generateTabPath = (tabLink: string) => {
    const id = route.params.id
    const subAccountId = activeSubAccountId.value
    const queryString = new URLSearchParams(
      route.query as Record<string, string>
    ).toString()
    const querySuffix = queryString ? `?${queryString}` : ''

    if (!id || !subAccountId) {
      return route.fullPath
    }

    const encodedSubAccountId = encodeURIComponent(subAccountId)

    const basePath = `/applications/${id}/${encodedSubAccountId}`
    if (tabLink === '') {
      return `${basePath}${querySuffix}`
    }
    return `${basePath}/${tabLink}${querySuffix}`
  }

  const tabs = computed(() => {
    const id = route.params.id
    const subAccountId = activeSubAccountId.value
    if (!id || !subAccountId) {
      return []
    }

    const isSSO = computed(() => route.query.sso === 'true')

    const tabs = [
      { title: t('overview'), path: generateTabPath('overview') },
      { title: t('accounts', { count: 2 }), path: generateTabPath('accounts') },
      { title: t('licenses'), path: generateTabPath('licenses') },
      { title: t('optimizations'), path: generateTabPath('optimizations') },
      { title: t('documents'), path: generateTabPath('documents') },
      { title: t('notes'), path: generateTabPath('notes') },
    ]

    return isSSO.value
      ? tabs.filter((tab) => tab.path !== generateTabPath('optimizations'))
      : tabs
  })
</script>
